import { groupTaskSteps } from "@/components/ui/task-steps-progress/groupTaskSteps";
import type { TaskStep } from "@/services/api/data-hub/types";

export type TaskStepProgress = {
  stepName: string;
  stepNumber: number;
  totalSteps: number;
  stepCurrent?: number | null;
  stepTotal?: number | null;
};

function displayStepName(label: string): string {
  const stripped = label.replace(/^Run /i, "").trim();
  if (!stripped) return label;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

function itemGroupProgress(items: TaskStep[]): { current: number; total: number } | null {
  if (items.length === 0) return null;
  const current = items.filter((s) => s.status === "done" || s.status === "failed").length;
  return { current, total: items.length };
}

/** Derive the active phase step and within-step progress from runner task steps. */
export function deriveTaskStepProgress(
  steps: TaskStep[],
  _overallProgress: number,
): TaskStepProgress | null {
  const phaseSteps = steps.filter((s) => s.kind !== "item");
  if (phaseSteps.length === 0) return null;

  const totalSteps = phaseSteps.length;
  const doneCount = phaseSteps.filter((s) => s.status === "done").length;
  const runningStep = phaseSteps.find((s) => s.status === "running" || s.status === "stalled");

  let stepNumber: number;
  let stepName: string;

  if (runningStep) {
    stepNumber = phaseSteps.indexOf(runningStep) + 1;
    stepName = runningStep.label;
  } else if (doneCount < totalSteps) {
    stepNumber = doneCount + 1;
    stepName = phaseSteps[doneCount]?.label ?? "Processing";
  } else {
    return null;
  }

  const blocks = groupTaskSteps(steps);
  const itemGroup = blocks.find((b) => b.kind === "item-group" && b.title === stepName);
  const itemProgress = itemGroup?.kind === "item-group" ? itemGroupProgress(itemGroup.steps) : null;

  return {
    stepName: displayStepName(stepName),
    stepNumber,
    totalSteps,
    stepCurrent: itemProgress?.current ?? null,
    stepTotal: itemProgress?.total ?? null,
  };
}

/** Runner-task sub-label (no mission Step N/M prefix). */
export function formatTaskStepSubLabel(progress: TaskStepProgress): string {
  if (progress.stepCurrent != null && progress.stepTotal != null) {
    return `${progress.stepName}: ${progress.stepCurrent}/${progress.stepTotal}`;
  }
  return `${progress.stepName} (${progress.stepNumber}/${progress.totalSteps})`;
}

/** @deprecated Prefer {@link formatTaskStepSubLabel} — kept for tests and callers expecting the old name. */
export function formatInProgressTaskLabel(progress: TaskStepProgress): string {
  return formatTaskStepSubLabel(progress);
}

export function buildInProgressLabelFromTask(
  steps: TaskStep[],
  overallProgress: number,
): string | null {
  const derived = deriveTaskStepProgress(steps, overallProgress);
  if (!derived) return null;
  return formatTaskStepSubLabel(derived);
}
